import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Card, CardBody, CardHeader, Heading, HStack, VStack, Text, Button,
  IconButton, Input, Select, Textarea, FormControl, FormLabel, FormHelperText,
  Badge, Divider, Tooltip, useToast, Collapse, SimpleGrid, Alert, AlertIcon,
  Wrap, WrapItem, Spinner,
} from '@chakra-ui/react';
import {
  AddIcon, ArrowUpIcon, ArrowDownIcon, DeleteIcon, CopyIcon,
  ChevronDownIcon, ChevronUpIcon, AttachmentIcon,
} from '@chakra-ui/icons';
import { useTranslation, Trans } from 'react-i18next';
import newsletterService from '../services/newsletterService';

const MODE_BLOCKS = 'blocks';
const MODE_HTML = 'html';

// Mirrors marketing/blocks.py DEFAULT_THEME. The server is authoritative and
// clamps anything out of range; these are only what a fresh campaign starts at.
const DEFAULT_THEME = {
  contentWidth: 600,
  pageBackground: '#f4f5f7',
  contentBackground: '#ffffff',
  fontFamily: 'Arial, Helvetica, sans-serif',
  textColor: '#2d3748',
  linkColor: '#2b6cb0',
};

// labelKey is translated where the badge/button renders (not at module load).
const BLOCK_META = {
  heading: { labelKey: 'emailBlockEditor.blockTypes.heading', icon: 'H', color: 'purple' },
  text: { labelKey: 'emailBlockEditor.blockTypes.text', icon: '¶', color: 'blue' },
  image: { labelKey: 'emailBlockEditor.blockTypes.image', icon: '▣', color: 'green' },
  button: { labelKey: 'emailBlockEditor.blockTypes.button', icon: '▭', color: 'orange' },
  divider: { labelKey: 'emailBlockEditor.blockTypes.divider', icon: '—', color: 'gray' },
  spacer: { labelKey: 'emailBlockEditor.blockTypes.spacer', icon: '⇕', color: 'gray' },
};

// Starter content for a freshly added block, in the operator's current language.
const NEW_BLOCK = {
  heading: (t) => ({ type: 'heading', text: t('emailBlockEditor.newBlock.headline'), level: 1, align: 'left' }),
  text: (t) => ({ type: 'text', text: t('emailBlockEditor.newBlock.text'), align: 'left', size: 15 }),
  image: () => ({ type: 'image', url: '', alt: '', width: 100, align: 'center', href: '' }),
  button: (t) => ({ type: 'button', label: t('emailBlockEditor.newBlock.buttonLabel'), url: 'https://', align: 'center', background: '#2b6cb0', color: '#ffffff', radius: 6 }),
  divider: () => ({ type: 'divider', color: '#e2e8f0', thickness: 1 }),
  spacer: () => ({ type: 'spacer', height: 24 }),
};

const FONT_STACKS = [
  { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
  { value: 'Georgia, Times New Roman, serif', label: 'Georgia' },
  { value: "'Helvetica Neue', Helvetica, Arial, sans-serif", label: 'Helvetica' },
  { value: 'Tahoma, Verdana, sans-serif', label: 'Tahoma' },
  { value: "'Trebuchet MS', Helvetica, sans-serif", label: 'Trebuchet' },
];

let blockSeq = 0;
const nextId = () => `b${Date.now().toString(36)}${(blockSeq += 1)}`;

// The block list is what a non-technical owner scans, so show the text the way
// it will read in the email — without the inline-markup punctuation.
const plainish = (text) => (text || '')
  .replace(/\[([^\]]+)\]\([^)\s]+\)/g, '$1')
  .replace(/\*\*(.+?)\*\*/g, '$1')
  .replace(/\*(.+?)\*/g, '$1');

const summarise = (block, t) => {
  switch (block.type) {
    case 'heading':
    case 'text':
      return plainish(block.text).split('\n')[0].slice(0, 70) || t('emailBlockEditor.summary.empty');
    case 'image':
      return block.url ? (block.alt || block.url.split('/').pop()) : t('emailBlockEditor.summary.noImage');
    case 'button':
      return t('emailBlockEditor.summary.button', {
        label: block.label || t('emailBlockEditor.summary.noLabel'),
        url: block.url || t('emailBlockEditor.summary.noLink'),
      });
    case 'divider':
      return t('emailBlockEditor.summary.divider', { px: block.thickness });
    case 'spacer':
      return t('emailBlockEditor.summary.spacer', { px: block.height });
    default:
      return '';
  }
};

/**
 * Visual newsletter body editor.
 *
 * Controlled: `value` is {bodyMode, bodyDesign, bodyHtml} and every edit calls
 * `onChange` with the whole next value. Deliberately does NOT render the email
 * itself — compilation to email-safe HTML lives server-side in
 * marketing/blocks.py so there is exactly one implementation. Use the
 * "Preview & Test" tab to see the real thing.
 */
const EmailBlockEditor = ({ value, onChange }) => {
  const { t } = useTranslation();
  const mode = value?.bodyMode || MODE_BLOCKS;
  const design = value?.bodyDesign || {};
  const theme = { ...DEFAULT_THEME, ...(design.theme || {}) };
  const blocks = design.blocks || [];

  const [expandedId, setExpandedId] = useState(null);
  const [showTheme, setShowTheme] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputRef = useRef(null);
  const pendingImageBlock = useRef(null);
  const toast = useToast();

  useEffect(() => {
    newsletterService.templates()
      .then((d) => setTemplates(d.templates || []))
      .catch(() => setTemplates([]));
  }, []);

  const emit = (patch) => onChange({ ...value, bodyMode: mode, ...patch });
  const setDesign = (nextDesign) => emit({ bodyDesign: nextDesign });
  const setBlocks = (nextBlocks) => setDesign({ theme, blocks: nextBlocks });
  const setTheme = (patch) => setDesign({ theme: { ...theme, ...patch }, blocks });

  const updateBlock = (id, patch) =>
    setBlocks(blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)));

  const addBlock = (type) => {
    const block = { id: nextId(), ...NEW_BLOCK[type](t) };
    setBlocks([...blocks, block]);
    setExpandedId(block.id);
  };

  const duplicateBlock = (id) => {
    const index = blocks.findIndex((b) => b.id === id);
    if (index < 0) return;
    const copy = { ...blocks[index], id: nextId() };
    const next = [...blocks];
    next.splice(index + 1, 0, copy);
    setBlocks(next);
  };

  const removeBlock = (id) => setBlocks(blocks.filter((b) => b.id !== id));

  const moveBlock = (id, delta) => {
    const index = blocks.findIndex((b) => b.id === id);
    const target = index + delta;
    if (index < 0 || target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    [next[index], next[target]] = [next[target], next[index]];
    setBlocks(next);
  };

  const applyTemplate = (templateId) => {
    const template = templates.find((tpl) => tpl.id === templateId);
    if (!template) return;
    // Re-key the blocks: template ids are shared across every hotel that picks
    // the same starter, and duplicates would break move/delete targeting.
    const next = (template.design.blocks || []).map((b) => ({ ...b, id: nextId() }));
    onChange({
      ...value,
      bodyMode: MODE_BLOCKS,
      bodyDesign: { theme: { ...DEFAULT_THEME, ...(template.design.theme || {}) }, blocks: next },
      // Only fill the subject if the operator hasn't written one.
      ...(template.subject && !value?.subject ? { subject: template.subject } : {}),
    });
    setExpandedId(null);
  };

  const pickImage = (blockId) => {
    pendingImageBlock.current = blockId;
    fileInputRef.current?.click();
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';           // let the same file be re-picked later
    const blockId = pendingImageBlock.current;
    if (!file || !blockId) return;
    setUploadingId(blockId);
    try {
      const res = await newsletterService.uploadImage(file);
      updateBlock(blockId, { url: res.url, alt: file.name.replace(/\.[^.]+$/, '') });
      toast({ title: t('emailBlockEditor.toasts.imageUploaded'), status: 'success', duration: 2000 });
    } catch (err) {
      toast({
        title: t('emailBlockEditor.toasts.uploadFailed'),
        description: err.response?.data?.detail || t('emailBlockEditor.toasts.uploadFailedHint'),
        status: 'error', duration: 6000,
      });
    } finally {
      setUploadingId(null);
    }
  };

  const switchToHtml = () => {
    // body_html already holds the compiled output of the current blocks (the
    // server recompiles on every save), so the raw editor opens on what the
    // operator was just looking at rather than a blank box.
    emit({ bodyMode: MODE_HTML });
    toast({
      title: t('emailBlockEditor.toasts.switchedToHtml'),
      description: t('emailBlockEditor.toasts.switchedToHtmlHint'),
      status: 'info', duration: 6000,
    });
  };

  if (mode === MODE_HTML) {
    return (
      <VStack align="stretch" spacing={3}>
        <Alert status="info" borderRadius="md" fontSize="sm">
          <AlertIcon />
          <Box flex={1}>
            {t('emailBlockEditor.html.notice')}
          </Box>
          <Button size="xs" ml={3} onClick={() => emit({ bodyMode: MODE_BLOCKS })}>
            {t('emailBlockEditor.html.backToBlocks')}
          </Button>
        </Alert>
        <FormControl>
          <FormLabel fontSize="sm">{t('emailBlockEditor.html.bodyLabel')}</FormLabel>
          <Textarea size="sm" rows={20} fontFamily="mono" fontSize="xs"
            value={value?.bodyHtml || ''}
            onChange={(e) => emit({ bodyHtml: e.target.value })} />
          <FormHelperText fontSize="xs">
            <Trans
              i18nKey="emailBlockEditor.html.unsubscribeHelp"
              values={{ unsubscribeTag: '{{unsubscribe_url}}' }}
              components={{ code: <code /> }}
            />
          </FormHelperText>
        </FormControl>
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing={4}>
      <input type="file" ref={fileInputRef} onChange={handleFile}
        accept="image/png,image/jpeg,image/gif,image/webp" style={{ display: 'none' }} />

      {/* Template + theme */}
      <Card shadow="sm" borderWidth="1px">
        <CardBody py={3}>
          <HStack spacing={3} flexWrap="wrap">
            <FormControl maxW="260px">
              <FormLabel fontSize="xs" mb={1}>{t('emailBlockEditor.template.label')}</FormLabel>
              <Select size="sm" defaultValue=""
                onChange={(e) => { applyTemplate(e.target.value); e.target.value = ''; }}>
                <option value="">{t('emailBlockEditor.template.choose')}</option>
                {templates.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                ))}
              </Select>
            </FormControl>
            <Box flex={1} />
            <Button size="xs" variant="ghost"
              rightIcon={showTheme ? <ChevronUpIcon /> : <ChevronDownIcon />}
              onClick={() => setShowTheme((v) => !v)}>
              {t('emailBlockEditor.theme.toggle')}
            </Button>
            <Button size="xs" variant="ghost" onClick={switchToHtml}>
              &lt;/&gt; {t('emailBlockEditor.html.edit')}
            </Button>
          </HStack>

          <Collapse in={showTheme} animateOpacity>
            <Divider my={3} />
            <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={3}>
              <FormControl>
                <FormLabel fontSize="xs" mb={1}>{t('emailBlockEditor.theme.width')}</FormLabel>
                <Input size="sm" type="number" value={theme.contentWidth}
                  onChange={(e) => setTheme({ contentWidth: Number(e.target.value) })} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" mb={1}>{t('emailBlockEditor.theme.font')}</FormLabel>
                <Select size="sm" value={theme.fontFamily}
                  onChange={(e) => setTheme({ fontFamily: e.target.value })}>
                  {FONT_STACKS.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </Select>
              </FormControl>
              <ColourField label={t('emailBlockEditor.theme.page')} value={theme.pageBackground}
                onChange={(v) => setTheme({ pageBackground: v })} />
              <ColourField label={t('emailBlockEditor.theme.card')} value={theme.contentBackground}
                onChange={(v) => setTheme({ contentBackground: v })} />
              <ColourField label={t('emailBlockEditor.fields.textColour')} value={theme.textColor}
                onChange={(v) => setTheme({ textColor: v })} />
              <ColourField label={t('emailBlockEditor.theme.links')} value={theme.linkColor}
                onChange={(v) => setTheme({ linkColor: v })} />
            </SimpleGrid>
          </Collapse>
        </CardBody>
      </Card>

      {/* Block list */}
      <Card shadow="sm" borderWidth="1px">
        <CardHeader pb={2}>
          <HStack justify="space-between">
            <Heading size="sm">{t('emailBlockEditor.list.title')}</Heading>
            <Text fontSize="xs" color="gray.500">
              {t('emailBlockEditor.list.blockCount', { count: blocks.length })}
            </Text>
          </HStack>
        </CardHeader>
        <CardBody pt={0}>
          <VStack align="stretch" spacing={2}>
            {blocks.map((block, index) => {
              const meta = BLOCK_META[block.type] || { label: block.type, icon: '?', color: 'gray' };
              const isOpen = expandedId === block.id;
              return (
                <Box key={block.id} borderWidth="1px" borderRadius="md"
                  borderColor={isOpen ? 'blue.300' : 'gray.200'} overflow="hidden">
                  <HStack px={3} py={2} bg={isOpen ? 'blue.50' : 'white'} spacing={3}
                    cursor="pointer" onClick={() => setExpandedId(isOpen ? null : block.id)}>
                    <Badge colorScheme={meta.color} fontSize="2xs" minW="52px" textAlign="center">
                      {meta.icon} {meta.labelKey ? t(meta.labelKey) : meta.label}
                    </Badge>
                    <Text fontSize="sm" flex={1} noOfLines={1} color="gray.700">
                      {summarise(block, t)}
                    </Text>
                    <HStack spacing={0} onClick={(e) => e.stopPropagation()}>
                      <Tooltip label={t('emailBlockEditor.actions.moveUp')}>
                        <IconButton size="xs" variant="ghost" aria-label={t('emailBlockEditor.actions.moveUp')}
                          icon={<ArrowUpIcon />} isDisabled={index === 0}
                          onClick={() => moveBlock(block.id, -1)} />
                      </Tooltip>
                      <Tooltip label={t('emailBlockEditor.actions.moveDown')}>
                        <IconButton size="xs" variant="ghost" aria-label={t('emailBlockEditor.actions.moveDown')}
                          icon={<ArrowDownIcon />} isDisabled={index === blocks.length - 1}
                          onClick={() => moveBlock(block.id, 1)} />
                      </Tooltip>
                      <Tooltip label={t('emailBlockEditor.actions.duplicate')}>
                        <IconButton size="xs" variant="ghost" aria-label={t('emailBlockEditor.actions.duplicate')}
                          icon={<CopyIcon />} onClick={() => duplicateBlock(block.id)} />
                      </Tooltip>
                      <Tooltip label={t('emailBlockEditor.actions.delete')}>
                        <IconButton size="xs" variant="ghost" colorScheme="red"
                          aria-label={t('emailBlockEditor.actions.delete')} icon={<DeleteIcon />}
                          onClick={() => removeBlock(block.id)} />
                      </Tooltip>
                    </HStack>
                  </HStack>

                  <Collapse in={isOpen} animateOpacity>
                    <Box px={3} py={3} borderTopWidth="1px" bg="gray.50">
                      <BlockFields
                        block={block}
                        onChange={(patch) => updateBlock(block.id, patch)}
                        onPickImage={() => pickImage(block.id)}
                        uploading={uploadingId === block.id}
                      />
                    </Box>
                  </Collapse>
                </Box>
              );
            })}

            {blocks.length === 0 && (
              <Box py={8} textAlign="center" color="gray.400" fontSize="sm"
                borderWidth="1px" borderStyle="dashed" borderRadius="md">
                {t('emailBlockEditor.list.empty')}
              </Box>
            )}
          </VStack>

          <Divider my={4} />

          <Wrap spacing={2}>
            {Object.entries(BLOCK_META).map(([type, meta]) => (
              <WrapItem key={type}>
                <Button size="xs" variant="outline" leftIcon={<AddIcon />}
                  onClick={() => addBlock(type)}>
                  {t(meta.labelKey)}
                </Button>
              </WrapItem>
            ))}
          </Wrap>
        </CardBody>
      </Card>
    </VStack>
  );
};

const ColourField = ({ label, value, onChange }) => (
  <FormControl>
    <FormLabel fontSize="xs" mb={1}>{label}</FormLabel>
    <HStack spacing={1}>
      <Input size="sm" type="color" p={0} w="34px" minW="34px" h="32px"
        value={value} onChange={(e) => onChange(e.target.value)} />
      <Input size="sm" value={value} onChange={(e) => onChange(e.target.value)} />
    </HStack>
  </FormControl>
);

const AlignPicker = ({ value, onChange }) => {
  const { t } = useTranslation();
  return (
    <FormControl>
      <FormLabel fontSize="xs" mb={1}>{t('emailBlockEditor.fields.align')}</FormLabel>
      <Select size="sm" value={value || 'left'} onChange={(e) => onChange(e.target.value)}>
        <option value="left">{t('emailBlockEditor.fields.alignLeft')}</option>
        <option value="center">{t('emailBlockEditor.fields.alignCenter')}</option>
        <option value="right">{t('emailBlockEditor.fields.alignRight')}</option>
      </Select>
    </FormControl>
  );
};

const BlockFields = ({ block, onChange, onPickImage, uploading }) => {
  const { t } = useTranslation();
  const set = (field) => (e) => onChange({ [field]: e.target.value });
  const setNum = (field) => (e) => onChange({ [field]: Number(e.target.value) });

  if (block.type === 'heading') {
    return (
      <VStack align="stretch" spacing={3}>
        <FormControl>
          <FormLabel fontSize="xs" mb={1}>{t('emailBlockEditor.fields.headingText')}</FormLabel>
          <Input size="sm" bg="white" value={block.text} onChange={set('text')} />
        </FormControl>
        <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
          <FormControl>
            <FormLabel fontSize="xs" mb={1}>{t('emailBlockEditor.fields.size')}</FormLabel>
            <Select size="sm" bg="white" value={block.level} onChange={setNum('level')}>
              <option value={1}>{t('emailBlockEditor.fields.sizeLarge')}</option>
              <option value={2}>{t('emailBlockEditor.fields.sizeMedium')}</option>
              <option value={3}>{t('emailBlockEditor.fields.sizeSmall')}</option>
            </Select>
          </FormControl>
          <AlignPicker value={block.align} onChange={(v) => onChange({ align: v })} />
          <ColourField label={t('emailBlockEditor.fields.colour')} value={block.color || '#2d3748'}
            onChange={(v) => onChange({ color: v })} />
        </SimpleGrid>
      </VStack>
    );
  }

  if (block.type === 'text') {
    return (
      <VStack align="stretch" spacing={3}>
        <FormControl>
          <FormLabel fontSize="xs" mb={1}>{t('emailBlockEditor.fields.text')}</FormLabel>
          <Textarea size="sm" bg="white" rows={5} value={block.text} onChange={set('text')} />
          <FormHelperText fontSize="xs">
            <Trans
              i18nKey="emailBlockEditor.fields.formattingHelp"
              values={{ firstNameTag: '{{guest_first_name}}' }}
              components={{ code: <code /> }}
            />
          </FormHelperText>
        </FormControl>
        <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
          <FormControl>
            <FormLabel fontSize="xs" mb={1}>{t('emailBlockEditor.fields.fontSize')}</FormLabel>
            <Input size="sm" bg="white" type="number" value={block.size}
              onChange={setNum('size')} />
          </FormControl>
          <AlignPicker value={block.align} onChange={(v) => onChange({ align: v })} />
          <ColourField label={t('emailBlockEditor.fields.colour')} value={block.color || '#2d3748'}
            onChange={(v) => onChange({ color: v })} />
        </SimpleGrid>
      </VStack>
    );
  }

  if (block.type === 'image') {
    return (
      <VStack align="stretch" spacing={3}>
        <FormControl>
          <FormLabel fontSize="xs" mb={1}>{t('emailBlockEditor.blockTypes.image')}</FormLabel>
          <HStack>
            <Input size="sm" bg="white" value={block.url} onChange={set('url')}
              placeholder={t('emailBlockEditor.fields.imageUrlPlaceholder')} />
            <Button size="sm" leftIcon={uploading ? <Spinner size="xs" /> : <AttachmentIcon />}
              onClick={onPickImage} isDisabled={uploading} flexShrink={0}>
              {t('emailBlockEditor.fields.upload')}
            </Button>
          </HStack>
        </FormControl>
        {block.url && (
          <Box borderWidth="1px" borderRadius="md" bg="white" p={2} textAlign="center">
            <img src={block.url} alt={block.alt}
              style={{ maxHeight: 140, maxWidth: '100%', display: 'inline-block' }} />
          </Box>
        )}
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
          <FormControl gridColumn={{ md: 'span 2' }}>
            <FormLabel fontSize="xs" mb={1}>{t('emailBlockEditor.fields.altText')}</FormLabel>
            <Input size="sm" bg="white" value={block.alt} onChange={set('alt')}
              placeholder={t('emailBlockEditor.fields.altTextPlaceholder')} />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="xs" mb={1}>{t('emailBlockEditor.fields.widthPercent')}</FormLabel>
            <Input size="sm" bg="white" type="number" value={block.width}
              onChange={setNum('width')} />
          </FormControl>
          <AlignPicker value={block.align} onChange={(v) => onChange({ align: v })} />
        </SimpleGrid>
        <FormControl>
          <FormLabel fontSize="xs" mb={1}>{t('emailBlockEditor.fields.imageLink')}</FormLabel>
          <Input size="sm" bg="white" value={block.href} onChange={set('href')}
            placeholder="https://…" />
        </FormControl>
      </VStack>
    );
  }

  if (block.type === 'button') {
    return (
      <VStack align="stretch" spacing={3}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <FormControl>
            <FormLabel fontSize="xs" mb={1}>{t('emailBlockEditor.fields.buttonLabel')}</FormLabel>
            <Input size="sm" bg="white" value={block.label} onChange={set('label')} />
          </FormControl>
          <FormControl isInvalid={!block.url || block.url === 'https://'}>
            <FormLabel fontSize="xs" mb={1}>{t('emailBlockEditor.fields.buttonLink')}</FormLabel>
            <Input size="sm" bg="white" value={block.url} onChange={set('url')}
              placeholder="https://yourhotel.com/book" />
            <FormHelperText fontSize="xs">
              {t('emailBlockEditor.fields.buttonLinkHelp')}
            </FormHelperText>
          </FormControl>
        </SimpleGrid>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
          <ColourField label={t('emailBlockEditor.fields.background')} value={block.background}
            onChange={(v) => onChange({ background: v })} />
          <ColourField label={t('emailBlockEditor.fields.textColour')} value={block.color}
            onChange={(v) => onChange({ color: v })} />
          <FormControl>
            <FormLabel fontSize="xs" mb={1}>{t('emailBlockEditor.fields.cornerRadius')}</FormLabel>
            <Input size="sm" bg="white" type="number" value={block.radius}
              onChange={setNum('radius')} />
          </FormControl>
          <AlignPicker value={block.align} onChange={(v) => onChange({ align: v })} />
        </SimpleGrid>
      </VStack>
    );
  }

  if (block.type === 'divider') {
    return (
      <SimpleGrid columns={{ base: 2 }} spacing={3} maxW="360px">
        <ColourField label={t('emailBlockEditor.fields.colour')} value={block.color}
          onChange={(v) => onChange({ color: v })} />
        <FormControl>
          <FormLabel fontSize="xs" mb={1}>{t('emailBlockEditor.fields.thickness')}</FormLabel>
          <Input size="sm" bg="white" type="number" value={block.thickness}
            onChange={setNum('thickness')} />
        </FormControl>
      </SimpleGrid>
    );
  }

  if (block.type === 'spacer') {
    return (
      <FormControl maxW="200px">
        <FormLabel fontSize="xs" mb={1}>{t('emailBlockEditor.fields.heightPx')}</FormLabel>
        <Input size="sm" bg="white" type="number" value={block.height}
          onChange={setNum('height')} />
      </FormControl>
    );
  }

  return null;
};

export { DEFAULT_THEME, MODE_BLOCKS, MODE_HTML };
export default EmailBlockEditor;
